const isServer = typeof window === 'undefined';

const AB_GENERAL = 'general';

/**
 * Initializes the AB testing configuration.
 * - If running on the server, it does nothing.
 * - If AB testing data exists in localStorage, it retrieves and parses it.
 * - Otherwise, it assigns a group ('a' or 'general') based on a 30/70 split and
 *   stores it in localStorage.
 * @returns {object} The AB testing configuration object.
 */
export function init() {
  if (isServer) return;
  const abTesting = localStorage.getItem('abTesting');

  if (abTesting) {
    return JSON.parse(abTesting);
  }

  // 30% chance to be in group A, 70% chance to be in the general group.
  const selectedGroup = Math.random() < 0.3 ? 'a' : AB_GENERAL;

  const data = {
    group: selectedGroup,
    version: '1.0.0'
  };

  localStorage.setItem('abTesting', JSON.stringify(data));

  return data;
}

/**
 * Retrieves the AB testing configuration.
 * - If running on the server, defaults to the 'general' group.
 * - If a query parameter 'ab-testing' exists, it overrides the group.
 * @returns {object} The AB testing configuration object.
 */
export function getABTesting() {
  if (isServer) return { group: AB_GENERAL };

  const data = init();

  const abTesting = new URLSearchParams(location.search).get('ab-testing');
  if (abTesting !== null) {
    return {
      ...data,
      group: abTesting || 'a'
    };
  }

  return data;
}

/**
 * Determines if the current user is a target for AB testing.
 * - Checks if the group is not 'general'.
 * @returns {boolean} True if the user is a target, false otherwise.
 */
export function isABTestingTarget() {
  return getABTesting().group !== AB_GENERAL;
}

/**
 * Sets a custom attribute on the Plausible analytics script tag to indicate
 * the current A/B testing state. This function modifies the `#plausible-script`
 * element by adding or updating the `event-abtest` attribute.
 *
 * This function does nothing if executed on the server-side.
 */
export function setEventOnPlausibleTag() {
  if (isServer) return;

  document
    .querySelector('#plausible-script')
    ?.setAttribute(
      'event-abtest',
      isABTestingTarget() ? 'Homepage CTA' : 'Control'
    );
}
